// /keys (docs/08-app-design.md 5.9), shown as "Protection": first what the
// person can rely on (recovery, this device, the automatic key refresh), then
// the technical record below it: the signing keys (tree index, keys used of
// 256, the counter, "Refresh now"), the retired trees, and the action history
// (every ledger record: purpose, status, digest short form, transaction link,
// expiry). Export "pending.json" with the sentence the SDK's design
// requires. "Forget this device" wipes IndexedDB after typing FORGET; it
// does not touch the vault. Presentation only: nothing here signs or spends.

import { type LeafRecord, type TreeMarker, type VaultSigner } from '@thevault/sdk';
import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { forgetConfirmDisabled, olderReserveClause, olderReserveForgetSentence, useLegacyReserve } from '../components/ForgetSheet';
import { KeyRefresh } from '../components/KeyRefresh';
import { Button, Field, KeyRuler, Kv, Notice, Sheet, StatusLine } from '../components/primitives';
import { Shell } from '../components/Shell';
import { type ChainState, EXPORT_NOTE, type Engine, type KeyStatus, describe } from '../engine';
import { session, useSession } from '../session';
import { explorerBase, explorerUrl, shortSig, slotsToText, whenText } from '../util';
import { statusTag } from './Vault';

interface KeysData {
  state: ChainState;
  signer: VaultSigner | null;
  keys: KeyStatus | null;
  history: LeafRecord[];
  retired: TreeMarker[];
  error: string | null;
}

async function load(engine: Engine): Promise<KeysData> {
  const state = await engine.readChain();
  if (state.kind !== 'vault' && state.kind !== 'tombstone') return { state, signer: null, keys: null, history: [], retired: [], error: null };
  try {
    const signer = await engine.signerFor(state);
    await engine.sync();
    const keys = await engine.keyStatus(signer);
    const history = await engine.history(signer);
    const retired: TreeMarker[] = [];
    for (let t = signer.treeIndex - 1; t >= Math.max(0, signer.treeIndex - 8); t--) {
      const m = await engine.store.getMarker(signer.vaultIdHex, t);
      if (m) retired.push(m);
    }
    return { state, signer, keys, history, retired, error: null };
  } catch (e) {
    return { state, signer: null, keys: null, history: [], retired: [], error: describe(e) };
  }
}

function purposeText(p: LeafRecord['purpose']): string {
  return { 'swap-exact': 'Swap', 'swap-bounded': 'Swap, bounded', transfer: 'Transfer', rotation: 'Key refresh', recovery: 'Key refresh', close: 'Close', other: 'Action' }[p];
}

export function Keys() {
  const { settings } = useSession();
  const [, navigate] = useLocation();
  const engine = session.engine();
  const [data, setData] = useState<KeysData | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [forget, setForget] = useState(false);
  // A legacy device still holds the per-device key of an earlier release, which Forget deletes (docs/18 section 3);
  // a failed read fails closed ('unknown': the device may hold one).
  const legacy = useLegacyReserve();
  const [forgetWord, setForgetWord] = useState('');
  const [exportUrl, setExportUrl] = useState<string | null>(null);

  const reload = useCallback(async () => setData(await load(engine)), [engine]);
  useEffect(() => {
    void reload();
  }, [reload]);
  useEffect(() => () => {
    if (exportUrl) URL.revokeObjectURL(exportUrl);
  }, [exportUrl]);

  const exportPending = async () => {
    if (!data?.signer) return;
    const payload = await engine.exportPending(data.signer);
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    setExportUrl(URL.createObjectURL(blob));
  };

  const checkUnresolved = async () => {
    if (!data?.signer) return;
    await engine.reconcile(data.signer);
    await reload();
  };

  const doForget = async () => {
    if (await session.forget()) navigate('/welcome');
  };

  const txBase = explorerBase(settings.rpcUrl, 'tx');
  const keys = data?.keys ?? null;
  // Read from the same key status the rest of the page shows; wording only.
  const refreshDue = keys ? keys.refreshReason !== null || keys.mode !== 'normal' : false;
  const refreshIn = keys ? (keys.actionsLeft === 0 ? 'with your next action' : `in ${keys.actionsLeft} ${keys.actionsLeft === 1 ? 'action' : 'actions'}`) : '';
  return (
    <Shell number="05" title="Protection" detail={keys ? `TREE ${keys.treeIndex} · ${keys.usedLeaves} OF 256 USED` : 'READING'}>
      <div className="console-heading">
        <h1>Keys and recovery.</h1>
        {keys ? <span className="dim">{keys.mode === 'normal' ? 'Normal' : 'Refresh required'}</span> : null}
      </div>
      <p className="lede">
        Every action is signed with its own one-time key. <Link href="/verify">Check the deployed program</Link> · <Link href="/protection">How protection works</Link>
      </p>
      <p className="scope-line">
        Quantum resistance covers the vault&rsquo;s spending authority. Solana itself, the fee reserve and today&rsquo;s upgrade key use ordinary Ed25519 keys; until that key is removed, the vault is only as quantum-resistant as that key.
      </p>
      {!data ? <p>Reading the ledger…</p> : null}
      {data?.error ? (
        <StatusLine state="failed" word="Key not usable.">
          {data.error}
        </StatusLine>
      ) : null}
      {data && data.state.kind === 'absent' ? (
        <Notice label="Not yet activated">
          <p className="ink">The vault does not exist on chain yet. Its first key tree becomes active when you activate it from the overview.</p>
        </Notice>
      ) : null}

      {data && keys && data.signer && (data.state.kind === 'vault' || data.state.kind === 'tombstone') ? (
        <>
          <section className="panel" aria-labelledby="access-title">
            <div className="console-heading">
              <h3 id="access-title">Your vault&apos;s access</h3>
            </div>
            <Kv
              rows={[
                ['Recovery', 'Your 24 words restore this vault on any device.'],
                ['This device', `Unlocks with this device's passphrase; forget it any time. ${refreshDue ? 'A key refresh runs before your next action.' : 'No key refresh is needed right now.'}`],
                ['Automatic key refresh', refreshIn],
              ]}
            />
          </section>

          <section className="panel" aria-labelledby="signing-title">
            <div className="console-heading">
              <h3 id="signing-title">Signing keys</h3>
              <span className="dim">Tree {keys.treeIndex}</span>
            </div>
            <div className="key-value">
              <strong>{keys.actionsLeft}</strong>
              <span>Actions until the next automatic key refresh</span>
            </div>
            <KeyRuler usedLeaves={data.state.snap.usedLeaves} label={`Tree ${keys.treeIndex}: ${keys.usedLeaves} of 256 one-time keys used`} />
            <div className="ruler-labels">
              <span>{keys.usedLeaves} used</span>
              <span>Key 256 is kept for refreshes</span>
              <span>256 key capacity</span>
            </div>
            <p>Each tick counts one one-time key used on this tree. The vault refreshes to a new tree automatically, so this is upkeep information, not a limit you have to manage.</p>
            <Kv
              rows={[
                ['Key tree', `${keys.treeIndex}`],
                ['Keys used', `${keys.usedLeaves} of 256; the vault refreshes automatically at 200`],
                ['Tree marker', keys.marker ? `held by this device since ${whenText(keys.marker.createdAt)} (${keys.marker.origin}); fresh for ${keys.markerExpiresInSlots !== null && keys.markerExpiresInSlots > 0n ? slotsToText(keys.markerExpiresInSlots) : 'no longer, a refresh runs first'}` : 'not held by this device'],
                ['Mode', keys.refreshReason ?? 'Normal: the next action signs with a fresh key.'],
              ]}
            />
            <Notice label="Refresh" tone={keys.refreshReason ? 'warn' : undefined}>
              {refreshing ? (
                <KeyRefresh
                  engine={engine}
                  signer={data.signer}
                  reason={keys.refreshReason ?? 'Refreshing now retires the current key tree and installs the next one. One small transaction, paid from the fee reserve.'}
                  onDone={() => {
                    setRefreshing(false);
                    void reload();
                  }}
                />
              ) : (
                <>
                  <p className="ink">{keys.refreshReason ?? 'A refresh retires this key tree and installs the next one. The vault does this on its own at 200 used keys, after a failed attempt, and after a long time away.'}</p>
                  <div className="actions">
                    <Button small variant={keys.refreshReason ? 'primary' : 'ghost'} onClick={() => setRefreshing(true)}>
                      Refresh now
                    </Button>
                  </div>
                </>
              )}
            </Notice>
          </section>

          {data.retired.length ? (
            <section className="panel" aria-labelledby="retired-title">
              <div className="console-heading">
                <h3 id="retired-title">Retired trees</h3>
                <span className="dim">{String(data.retired.length).padStart(2, '0')}</span>
              </div>
              <Kv rows={data.retired.map((m) => [`Tree ${m.treeIndex}`, `${m.origin} on this device, ${whenText(m.createdAt)}; retired`])} />
            </section>
          ) : null}

          <section className="panel" aria-labelledby="history-title">
            <div className="console-heading">
              <h3 id="history-title">Action history</h3>
              <span className="dim">{data.history.length ? `${data.history.length} ${data.history.length === 1 ? 'record' : 'records'}` : 'Nothing yet'}</span>
            </div>
            {keys.unresolved.length ? (
              <div className="actions">
                <Button small onClick={() => void checkUnresolved()}>
                  Check again
                </Button>
              </div>
            ) : null}
            {data.history.length ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Key</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.history.map((r) => {
                    const t = statusTag(r.status, r.note, r.purpose);
                    return (
                      <tr key={r.key}>
                        <td>
                          {purposeText(r.purpose)}
                          <small>
                            digest {r.digest.slice(0, 12)}… · {whenText(r.updatedAt)}
                            {r.note ? ` · ${r.note}` : ''}
                          </small>
                        </td>
                        <td>
                          {r.leaf} / tree {r.treeIndex}
                          <small>{r.validUntilSlot === '18446744073709551615' ? 'never expires' : `expires at slot ${r.validUntilSlot}`}</small>
                        </td>
                        <td>
                          <span className={`tag ${t.cls}`}>{t.word}</span>
                          {r.txSignature ? <small>{explorerUrl(txBase, r.txSignature) ? <a href={explorerUrl(txBase, r.txSignature)!} target="_blank" rel="noreferrer">{shortSig(r.txSignature)} ↗</a> : shortSig(r.txSignature)}</small> : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p>Every action this device signs will be listed here.</p>
            )}

            <div className="actions">
              <Button variant="ghost" small onClick={() => void exportPending()}>
                Export pending.json
              </Button>
              {exportUrl ? (
                <a className="button ghost small" href={exportUrl} download="pending.json">
                  Download
                </a>
              ) : null}
            </div>
            <p>{EXPORT_NOTE} One device at a time is the supported model: two devices acting at once on the same vault collide with a chance of about 1 in 200 per action.</p>
          </section>
        </>
      ) : null}

      <Notice label="This device">
        <p className="ink">
          Forget this device to delete its sealed key{olderReserveClause(legacy)} and its ledger. The vault and its fee reserve are not touched; the 24 words open both anywhere.
        </p>
        <div className="actions">
          <Button variant="danger" small onClick={() => setForget(true)}>
            Forget this device
          </Button>
        </div>
      </Notice>
      <Sheet open={forget} onClose={() => setForget(false)} title="Forget this device">
        <h2>Forget this device.</h2>
        <p>
          This deletes the app&rsquo;s data on this device only. Your fee reserve is not removed: your 24 words open it on any device.{legacy === false ? '' : ` ${olderReserveForgetSentence(legacy)}`}
        </p>
        <Field id="forget" label="Type FORGET to confirm">
          <input id="forget" className="input" value={forgetWord} onChange={(e) => setForgetWord(e.target.value)} autoComplete="off" />
        </Field>
        <div className="actions">
          <Button variant="danger" disabled={forgetConfirmDisabled(forgetWord, legacy)} onClick={() => void doForget()}>
            Forget this device
          </Button>
          <Button variant="ghost" onClick={() => setForget(false)}>
            Keep it
          </Button>
        </div>
      </Sheet>
    </Shell>
  );
}
